import {gql} from '@apollo/client';
import {notification} from 'antd';
import confetti from 'canvas-confetti';
import {MouseEventHandler, useCallback, useEffect, useRef, useState} from 'react';
import {useIsMounted, useSubscription} from 'src/hooks';
import {itoh, sleep} from 'src/utils';
import loadingSvg from './MintReveal.loading.svg';
import SocialShare from './SocialShare';
import styles from './MintReveal.module.scss';

export enum MintType {
  Art,
  Artist,
}

const GQL = {
  [MintType.Art]: gql`
    subscription NewArt($id: ID!) {
      mint: art(subgraphError: allow, id: $id) {
        artist {
          name
          uri # Need this for metadata augmenter
        }
        realArtist
        title
        imageUri
      }
    }
  `,
  [MintType.Artist]: gql`
    subscription NewArtist($id: ID!) {
      mint: artist(subgraphError: allow, id: $id) {
        name
        description
        imageUri
      }
    }
  `,
};

type MintRevealProps = {
  id: string;
  mintType: MintType;
  onClose: MouseEventHandler<HTMLAnchorElement>;
};

const A_WHILE = 15 * 1000;
const IMAGE_LOADED = 1;
const IMAGE_ERRORED = 2;
export default function MintReveal(props: MintRevealProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [confettiFired, setConfettiFired] = useState<boolean>(false);
  const [imageStatus, setImageStatus] = useState<number>(0);
  const [hasBeenAWhile, setHasBeenAWhile] = useState<boolean>(false);
  const isMounted = useIsMounted();

  const {data} = useSubscription(GQL[props.mintType], {
    variables: {
      id: props.id.toString(),
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasBeenAWhile(true);
    }, A_WHILE);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    async function fireConfetti() {
      if (
        data?.mint?.imageUri &&
        canvasRef.current &&
        !confettiFired &&
        imageStatus === IMAGE_LOADED
      ) {
        const myConfetti = confetti.create(canvasRef.current, {resize: true});
        const confetti1 = myConfetti({
          spread: 80,
          angle: 55,
          ticks: 300,
          scalar: 1.5,
          origin: {x: 0.6, y: 0.5},
        });
        await sleep(600);
        const confetti2 = myConfetti({
          angle: 110,
          spread: 90,
          ticks: 300,
          scalar: 1.5,
          origin: {
            x: 0.35,
            y: 0.7,
          },
        });

        await Promise.all([confetti1, confetti2]);
        if (isMounted.current) {
          setConfettiFired(true);
        }
      }
    }
    fireConfetti();

    // Don't want to rerun this if `isMounted` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.mint?.imageUri, canvasRef, confettiFired, setConfettiFired, imageStatus]);

  const renderDescription = useCallback(() => {
    if (!data?.mint) {
      return null;
    }

    const {mint} = data;
    return (
      <div className={styles.description}>
        {props.mintType === MintType.Art ? (
          <>
            <h1>
              {mint.title} by {mint.realArtist}
            </h1>
            <h2>Created By: {mint.artist?.name}</h2>
          </>
        ) : (
          <>
            <h1>{mint.name}</h1>
            <p>{mint.description}</p>
          </>
        )}
      </div>
    );
  }, [data, props.mintType]);

  const isImageAvailable = !!data?.mint?.imageUri;
  const isImageLoaded = imageStatus === IMAGE_LOADED;

  useEffect(() => {
    if (isImageLoaded) {
      notification.close('minting');
    } else {
      notification.info({
        key: 'minting',
        message: `A new ${props.mintType === MintType.Art ? 'artwork' : 'artist'} is being minted!`,
        duration: null,
        icon: <img style={{height: 32}} src={loadingSvg} alt='loading' />,
      });
    }
  }, [isImageLoaded, props.mintType]);

  return (
    <div style={{display: isImageLoaded ? 'block' : 'none'}} className={styles.overlay}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a href='#' className={styles.close} onClick={props.onClose}>
        Close
      </a>
      <div className={styles.art}>
        {isImageAvailable && isImageLoaded ? (
          renderDescription()
        ) : (
          <div className={styles.loading}>
            {imageStatus === IMAGE_ERRORED ? (
              <h1>
                Oops, failed to load the mint.
                <br />
                No worries, it will appear soon in your collection.
              </h1>
            ) : (
              <>
                <h1>
                  Unveiling the new mint!
                  <br />
                  Artists love to be fashionably late, so this can take some time....
                </h1>
                {hasBeenAWhile && (
                  <p className='animate__animated animate__pulse animate__infinite'>
                    Looks like this is taking longer than usual. You can safely close this and your
                    mint will eventually show up. Artists... amirite?
                  </p>
                )}
                <div className={styles.loader}>
                  <img src={loadingSvg} alt='loading' />
                </div>
              </>
            )}
          </div>
        )}
        {isImageAvailable && (
          <>
            <img
              className={[
                styles.img,
                imageStatus === IMAGE_LOADED ? 'animate__animated animate__zoomIn' : styles.hidden,
              ].join(' ')}
              src={itoh(data?.mint?.imageUri)}
              alt={data?.mint?.name || data?.mint?.description}
              onLoad={() => setImageStatus(IMAGE_LOADED)}
              onError={() => setImageStatus(IMAGE_ERRORED)}
            />

            {isImageLoaded && (
              <SocialShare
                text={`Check out this new ${
                  props.mintType === MintType.Art ? 'artwork' : 'artist'
                } I just minted in the Lisa Mona NFT collection!`}
                path={`/${props.mintType === MintType.Art ? 'arts' : 'artists'}/${props.id}`}
              />
            )}
          </>
        )}
      </div>
      {/* Need to remove canvas to allow the description scrollbar to work */}
      {!confettiFired && <canvas ref={canvasRef} className={styles.canvas} />}
    </div>
  );
}
